import { useState } from 'react';

import Styles from './DashboardLayout.module.scss';
import ChartAcceptanceTime from './widgets/ChartAcceptanceTime';
import ChartResponseRatio from './widgets/ChartResponseRatio';
import ChartTotalActions from './widgets/ChartTotalActions';
import { ChartWidget } from './widgets/ChartWidget';
import { WithListItemStyle, ChartWidgetList } from './widgets/ChartWidgetList';
import GougeAcceptanceTime from './widgets/GougeAcceptanceTime';
import PieChartActionDistribution from './widgets/PieChartActionDistribution';
import PieChartResponseRatio from './widgets/PieChartResponseRatio';
import PieChartTotalActions from './widgets/PieChartTotalActions';
import { ResponseRatioFilter } from './widgets/ResponseRatioFilter';
import { TableTotalActions } from './widgets/TableTotalActions';
import { SortSwitch } from './widgets-elements/SortSwitch';
import { Widget } from './widgets-elements/Widget';

import type {
	AcceptanceTimeItem,
	ResponseRatioFilter as RatioFilter,
	ResponseRatioItem,
	TotalActionsPerOrganizationItem,
	TotalActionsRowItem,
} from '@/app/types/Dashboard';
import type { MessageContainerFilter } from '@/app/types/Filter';

import {
	useActionDashboardQueryData,
	useSelectedOrganization,
} from '@/app/hooks/Dashboard/useActionDashboardQuery';
import { useAppSelector } from '@/app/hooks/useAppSelector';
import { Select } from '@/components/Elements/SelectV2';
// const GougeAcceptanceTime = lazy(() => import('@/components/Dashboard/GougeAcceptanceTime'));
// const PieChartResponseRatio = lazy(() => import('@/components/Dashboard/PieChartResponseRatio'));
// const PieChartTotalActions = lazy(() => import('@/components/Dashboard/PieChartTotalActions'));
// const PieChartActionDistribution = lazy(
// 	() => import('@/components/Dashboard/PieChartActionDistribution')
// );

const ChartWidgetListItem = WithListItemStyle(ChartWidget);

export const DashboardLayout = () => {
	const filter = useAppSelector((state) => state.filter);

	return (
		<div className={Styles.container}>
			<div className={Styles.fullRow}>
				<ResponseRatioWidgetList filter={filter} />
			</div>

			<div className={Styles.fullRow}>
				<TotalActionsWidgetList filter={filter} />
			</div>
			<div className={Styles.fullRow}>
				<ActionDistributionWidgetList filter={filter} />
			</div>
			<div className={Styles.fullRow}>
				<ResponseTimeWidgetList filter={filter} />
			</div>

			<div className={Styles.fullRow}>
				<TotalActionsTableWidget filter={filter} />
			</div>
			<TotalActionsChartWidget filter={filter} />
			<AcceptanceTimeChartWidget filter={filter} />
			<div className={Styles.fullRow}>
				<ResponseRationChartWidget filter={filter} />
			</div>
		</div>
	);
};

interface WidgetItemProps {
	filter: MessageContainerFilter;
	organizationId: string;
}

const ResponseRatioWidgetList = ({ filter }: Pick<WidgetItemProps, 'filter'>) => {
	const { data, isLoading } = useActionDashboardQueryData(filter);
	const selectedOrganizations = useSelectedOrganization(filter);

	const responseRationMap: Record<string, ResponseRatioItem> = {};
	data?.responseRationSeries?.forEach((item) => {
		responseRationMap[item.organizationId] = item;
	});

	const responseRatios = selectedOrganizations.map((org) => ({
		organization: org.name,
		responseRatio: responseRationMap[org.id] || null,
	}));

	return (
		<Widget
			style={{
				minHeight: 272,
				height: 272,
			}}
		>
			<ChartWidgetList
				data={responseRatios}
				renderItem={(item) => (
					<ChartWidgetListItem
						title="Response Ratio"
						subtitle={item.organization}
						data={item.responseRatio}
						isLoading={isLoading}
					>
						{(responseRatio) => <PieChartResponseRatio data={responseRatio} />}
					</ChartWidgetListItem>
				)}
			/>
		</Widget>
	);
};

const TotalActionsWidgetList = ({ filter }: Pick<WidgetItemProps, 'filter'>) => {
	const { data, isLoading } = useActionDashboardQueryData(filter);
	const selectedOrganizations = useSelectedOrganization(filter);

	const totalActions = selectedOrganizations.map((org) => {
		const matchedResult = data?.totalActionsRows?.filter(
			(item: TotalActionsRowItem) => item.organizationId === org.id
		);

		const total = data?.totalActionsSeries?.find(
			(item) => item.organizationId === org.id
		)?.organizationTotal;

		return {
			organization: org.name,
			total,
			chartData: matchedResult ?? null,
		};
	});

	return (
		<Widget
			style={{
				minHeight: 363,
				height: 363,
			}}
		>
			<ChartWidgetList
				data={totalActions}
				renderItem={(item) => (
					<ChartWidgetListItem
						title={`Total Actions${item.total ? ` - ${item.total}` : ''}`}
						subtitle={item.organization}
						data={item.chartData}
						isLoading={isLoading}
					>
						{(totalAction) => <PieChartTotalActions data={totalAction} />}
					</ChartWidgetListItem>
				)}
			/>
		</Widget>
	);
};

const ActionDistributionWidgetList = ({ filter }: Pick<WidgetItemProps, 'filter'>) => {
	const { data, isLoading } = useActionDashboardQueryData(filter);
	const selectedOrganizations = useSelectedOrganization(filter);

	const actionDistributionMap: Record<string, TotalActionsPerOrganizationItem | null> =
		data?.totalActionsSeries.reduce(
			(acc: Record<string, TotalActionsPerOrganizationItem>, item) => {
				acc[item.organizationId] = item;
				return acc;
			},
			{}
		) ?? {};

	const items = selectedOrganizations.map((org) => ({
		organization: org.name,
		actionDistribution: actionDistributionMap[org.id] ?? null,
	}));

	return (
		<Widget
			style={{
				minHeight: 272,
				height: 272,
			}}
		>
			<ChartWidgetList
				data={items}
				renderItem={(item) => (
					<ChartWidgetListItem
						title="Actions Distribution"
						subtitle={item.organization}
						data={item.actionDistribution}
						isLoading={isLoading}
					>
						{(actionDistribution) => (
							<PieChartActionDistribution data={actionDistribution} />
						)}
					</ChartWidgetListItem>
				)}
			/>
		</Widget>
	);
};

const ResponseTimeWidgetList = ({ filter }: Pick<WidgetItemProps, 'filter'>) => {
	const { data, isLoading } = useActionDashboardQueryData(filter);
	const selectedOrganizations = useSelectedOrganization(filter);

	const acceptanceTimeMap: Record<string, AcceptanceTimeItem | null> =
		data?.acceptedTimeSeries.reduce((acc: Record<string, AcceptanceTimeItem>, item) => {
			acc[item.organizationId] = item;
			return acc;
		}, {}) ?? {};

	const items = selectedOrganizations.map((org) => ({
		organization: org.name,
		actionDistribution: acceptanceTimeMap[org.id] ?? null,
	}));

	return (
		<Widget
			style={{
				minHeight: 272,
				height: 272,
			}}
		>
			<ChartWidgetList
				data={items}
				renderItem={(item) => (
					<ChartWidgetListItem
						title="Response Time"
						subtitle={item.organization}
						data={item.actionDistribution}
						isLoading={isLoading}
					>
						{(acceptanceTime) => <GougeAcceptanceTime data={acceptanceTime} />}
					</ChartWidgetListItem>
				)}
			/>
		</Widget>
	);
};

const TotalActionsChartWidget = ({ filter }: Pick<WidgetItemProps, 'filter'>) => {
	const { data, isLoading } = useActionDashboardQueryData(filter);
	const [ascending, setAscending] = useState<boolean>(true);

	return (
		<Widget
			style={{
				minHeight: 328,
				height: 328,
				maxHeight: 328,
			}}
		>
			<ChartWidget
				actions={<SortSwitch value={ascending} onChange={setAscending} />}
				title="Number of Actions per Organization"
				data={data?.totalActionsSeries}
				isLoading={isLoading}
			>
				{(totalActions) => <ChartTotalActions data={totalActions} ascending={ascending} />}
			</ChartWidget>
		</Widget>
	);
};

const AcceptanceTimeChartWidget = ({ filter }: Pick<WidgetItemProps, 'filter'>) => {
	const { data, isLoading } = useActionDashboardQueryData(filter);
	const [ascending, setAscending] = useState<boolean>(true);

	return (
		<Widget
			style={{
				minHeight: 328,
				height: 328,
				maxHeight: 328,
			}}
		>
			<ChartWidget
				actions={<SortSwitch value={ascending} onChange={setAscending} />}
				title="Actions Acceptance Time"
				data={data?.acceptedTimeSeries}
				isLoading={isLoading}
			>
				{(acceptanceTime) => (
					<ChartAcceptanceTime data={acceptanceTime} ascending={ascending} />
				)}
			</ChartWidget>
		</Widget>
	);
};

const ResponseRationChartWidget = ({ filter }: Pick<WidgetItemProps, 'filter'>) => {
	const { data, isLoading } = useActionDashboardQueryData(filter);
	const [ascending, setAscending] = useState<boolean>(true);
	const [responseFilter, setResponseFilter] = useState<RatioFilter>('all');

	return (
		<Widget
			style={{
				minHeight: 382,
				height: 382,
				maxHeight: 382,
			}}
		>
			<ChartWidget
				actions={
					<div className={Styles.filter}>
						<ResponseRatioFilter value={responseFilter} onChange={setResponseFilter} />
						<SortSwitch value={ascending} onChange={(e) => setAscending(e)} />
					</div>
				}
				title="Actions Response Ratio"
				data={data?.responseRationSeries}
				isLoading={isLoading}
			>
				{(responseRatio) => (
					<ChartResponseRatio
						data={responseRatio}
						ascending={ascending}
						filter={responseFilter}
					/>
				)}
			</ChartWidget>
		</Widget>
	);
};

const TotalActionsTableWidget = ({ filter }: Pick<WidgetItemProps, 'filter'>) => {
	const { isLoading, data: { totalActionsRows } = { totalActionsRows: [] } } =
		useActionDashboardQueryData(filter);
	const [selected, setSelected] = useState<string[]>([]);
	const organizations = useSelectedOrganization(filter);

	const data = totalActionsRows.filter((item) =>
		selected.length > 0 ? selected.includes(item.organizationId) : true
	);

	return (
		<Widget
			style={{
				height: 382,
				maxHeight: 382,
				minHeight: 382,
				overflow: 'hidden',
			}}
		>
			<div className={Styles.tableContainer}>
				<div className={Styles.header}>
					<p>Total Actions</p>
					<div>
						<Select value={selected} onChange={setSelected}>
							{organizations.map((item) => (
								<Select.Option value={item.id} key={item.id}>
									{item.name}
								</Select.Option>
							))}
						</Select>
					</div>
				</div>
				<div className={Styles.table}>
					<TableTotalActions data={data} isLoading={isLoading} />
				</div>
			</div>
		</Widget>
	);
};
